import { client } from "@/client";
import { qs } from "@/client/extend";
import SelectInput from "@/components/select-input/select-input";
import { cn, isDefined } from "@/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { Route } from "./route";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useRouterState, useSearch } from "@tanstack/react-router";
import { useSelectInputCache } from "@/state/select-input-cache-context";

import { showActionErrorToast } from "@/state/error-toast";

export const AccountPicker = () => {
  const { t } = useTranslation();
  const { queryClient } = Route.useRouteContext();
  const account = useQuery(client.users.me.asQueryOptions());
  const relatedAccounts = useQuery(
    client.users.getRelatedAccounts.asQueryOptions(),
  );
  const switchAccount = useMutation({
    ...client.auth.accountSwitch.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
  });
  const accountOptions = React.useMemo(
    () =>
      (relatedAccounts?.data ?? [])?.map((a) => ({
        label: a?.account?.name,
        value: a?.account?.id,
        icon: undefined,
      })),
    [relatedAccounts?.data],
  );
  const currentAccountOption = React.useMemo(
    () => accountOptions.find((a) => a.value === account.data?.accountId),
    [accountOptions, account.data],
  );
  const search = useSearch({
    // from: "/dashboard/unauthorized",
    strict: false,
  });
  const state = useRouterState({
    select: ({ matches, location }) => ({ location, matches }),
  });

  const { incrementCacheUniq } = useSelectInputCache();
  const navigate = Route.useNavigate();
  return (
    <SelectInput
      className="min-w-36"
      options={accountOptions}
      value={currentAccountOption}
      isClearable={false}
      isMulti={false}
      classNames={{ control: (_s) => cn("h-9") }}
      onChange={async (option) => {
        const accountId = option?.value;
        if (!isDefined(accountId) || accountId === account?.data?.accountId)
          return;
        // Validates access server-side; the actual "this tab is now on this account" state
        // change lives in the URL's accountId search param -- no shared cookie or per-tab
        // cache is written, so no other tab is affected.
        await switchAccount.mutateAsync({ requestBody: { accountId } });

        // Close all open dialogs by simulating Escape key
        document.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "Escape",
            code: "Escape",
            keyCode: 27,
            which: 27,
            bubbles: true,
            cancelable: true,
          }),
        );

        // Navigate away from resource-specific pages that might not exist in new account
        const resourceId = state.matches
          ?.at(-1)
          ?.routeId?.match(/\/(\$[^/]+)/)?.[0];
        const nonResourceIdMatch = resourceId
          ? state.matches.findLast((m) => !m.routeId.includes(resourceId))
          : undefined;

        if (search?.context) {
          // Land the new accountId in the URL first -- the request interceptor in
          // client/index.ts reads it from there, so clearing the cache before this would let an
          // in-flight refetch repopulate it under the old account.
          await navigate({
            to: search.context,
            search: { ...search, context: undefined, accountId } as any,
          });
          queryClient.clear();
          incrementCacheUniq();
        } else if (resourceId && nonResourceIdMatch) {
          // Hard reload -- no toast opportunity, page unloads immediately. Preserve the rest of
          // the current search params (e.g. `collapsed`) instead of dropping them.
          window.location.href = `${nonResourceIdMatch.fullPath}${qs({ ...search, accountId })}`;
          return;
        } else {
          await navigate({
            search: (prev: any) => ({ ...prev, accountId }),
            replace: true,
          } as any);
          queryClient.invalidateQueries();
          incrementCacheUniq();
        }

        toast.info(t("accountSwitched"));
      }}
    />
  );
};
